import type { SessionKind } from '@domain/chat/types';
import { getProto } from '../../im/pb-imports';

export const AUDIO_PREVIEW_TEXT = '[语音]';
export const AUDIO_RECORDING_MIN_MS = 500;
export const AUDIO_RECORDING_MAX_MS = 60000;
export const FLUTTER_COMPAT_AUDIO_MIME = 'audio/ogg;codecs=opus';
export const FLUTTER_COMPAT_AUDIO_EXTENSION = 'opus';

export type AudioAssetPayload = {
  kind: 'audio';
  version: 1;
  assetId: string;
  provider: 's3';
  url: string;
  meta?: {
    durationMs?: number;
    mimeType?: string;
    fileName?: string;
    size?: number;
  };
};

const AUDIO_RECORDING_MIME_CANDIDATES = [
  'audio/ogg;codecs=opus',
  'audio/webm;codecs=opus',
  'audio/webm',
] as const;

export function isSupportedFlutterCompatRecording() {
  if (typeof window === 'undefined' || typeof MediaRecorder === 'undefined') {
    return false;
  }
  return AUDIO_RECORDING_MIME_CANDIDATES.some((mimeType) => MediaRecorder.isTypeSupported(mimeType));
}

export function resolveSupportedAudioRecordingMimeType() {
  if (typeof window === 'undefined' || typeof MediaRecorder === 'undefined') {
    return null;
  }
  return AUDIO_RECORDING_MIME_CANDIDATES.find((mimeType) => MediaRecorder.isTypeSupported(mimeType)) ?? null;
}

export function buildAudioMessageData(audioBytes: Uint8Array, durationMs: number) {
  const header = new Uint8Array(4);
  const view = new DataView(header.buffer);
  view.setInt32(0, Math.max(1, Math.round(durationMs / 1000)), false);

  const payload = new Uint8Array(header.length + audioBytes.length);
  payload.set(header, 0);
  payload.set(audioBytes, header.length);
  return payload;
}

export function buildAudioAssetMessageData(payload: AudioAssetPayload) {
  return new TextEncoder().encode(JSON.stringify(payload));
}

export function parseAudioAssetPayload(msgData?: Uint8Array): AudioAssetPayload | null {
  if (!msgData || msgData.length === 0 || msgData[0] !== 123) {
    return null;
  }
  try {
    const parsed = JSON.parse(new TextDecoder().decode(msgData)) as Partial<AudioAssetPayload>;
    if (
      parsed.kind === 'audio'
      && parsed.version === 1
      && parsed.provider === 's3'
      && typeof parsed.assetId === 'string'
      && parsed.assetId.length > 0
      && typeof parsed.url === 'string'
    ) {
      return parsed as AudioAssetPayload;
    }
  } catch {
    return null;
  }
  return null;
}

export function extractAudioDurationSeconds(msgData?: Uint8Array) {
  const asset = parseAudioAssetPayload(msgData);
  if (asset?.meta?.durationMs) {
    return Math.max(1, Math.round(asset.meta.durationMs / 1000));
  }
  if (!msgData || msgData.length < 4) {
    return 0;
  }
  const view = new DataView(msgData.buffer, msgData.byteOffset, msgData.byteLength);
  return Math.max(0, view.getInt32(0, false));
}

export function extractAudioBytes(msgData?: Uint8Array) {
  if (parseAudioAssetPayload(msgData)) {
    return new Uint8Array();
  }
  if (!msgData || msgData.length <= 4) {
    return new Uint8Array();
  }
  return msgData.slice(4);
}

export function resolveAudioMsgType(sessionType: SessionKind) {
  const proto = getProto();
  return sessionType === 'group'
    ? proto.IM.BaseDefine.MsgType.MSG_TYPE_GROUP_AUDIO
    : proto.IM.BaseDefine.MsgType.MSG_TYPE_SINGLE_AUDIO;
}

export function isAudioMsgType(msgType?: number) {
  if (msgType == null) return false;
  const proto = getProto();
  return msgType === proto.IM.BaseDefine.MsgType.MSG_TYPE_SINGLE_AUDIO
    || msgType === proto.IM.BaseDefine.MsgType.MSG_TYPE_GROUP_AUDIO;
}
